import type { SelectChangeEvent } from "@mui/material";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import type { FC } from "react";
import { Link as RouterLink, useSearchParams } from "react-router";
import { EllipsisText } from "../../../modules/generic/components/ellipsis-text";
import { reviewStatusToPill, statusToPill } from "../../../modules/moment-card/utils";
import { LiveSearchField } from "../../../modules/search/live-search-field";
import { formatDate, fromISO8601 } from "../../../utils/iso8601";
import type { ReviewsVariant } from "../hooks/use-reviews";
import { statusFilterOptions, useReviews } from "../hooks/use-reviews";

const columnCount = 5;

export const ReviewsTable: FC<{ variant: ReviewsVariant }> = ({ variant }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") ?? "";
  const { reviews, loading, error } = useReviews(variant);

  const handleStatusChange = (event: SelectChangeEvent): void => {
    const params = new URLSearchParams(searchParams);
    event.target.value ? params.set("status", event.target.value) : params.delete("status");
    setSearchParams(params);
  };

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="reviews-status-filter-label">Status</InputLabel>
          <Select
            labelId="reviews-status-filter-label"
            label="Status"
            value={status}
            onChange={handleStatusChange}
          >
            <MenuItem value="">All</MenuItem>
            {statusFilterOptions[variant].map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ minWidth: 280, maxWidth: 400 }}>
          <LiveSearchField label="Search reviews" size="small" />
        </Box>
      </Stack>

      <TableContainer component={Paper}>
        {loading && <LinearProgress />}
        {error && <Alert severity="error">Unable to load reviews</Alert>}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Communication</TableCell>
              <TableCell>Creator</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && !error && reviews.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount}>
                  {variant === "previous"
                    ? "You have no previous reviews."
                    : "You have no Communications to review."}
                </TableCell>
              </TableRow>
            )}
            {reviews.map(
              ({ moment_id, title, momentStatus, reviewStatus, creator, requestedAt, message }) => (
                <TableRow key={moment_id}>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={
                        variant === "previous"
                          ? `/moments/${moment_id}/review`
                          : `/moments/${moment_id}`
                      }
                      data-analytics-id="reviews-communication-link"
                      underline="hover"
                      color="text.primary"
                    >
                      {title}
                    </Link>
                  </TableCell>
                  <TableCell>{creator}</TableCell>
                  <TableCell>
                    {/* If we are displaying previous-review we display moment status,
                    if we are displaying in-progress then show the review status  */}
                    {variant === "previous"
                      ? statusToPill(momentStatus)
                      : reviewStatusToPill(reviewStatus)}
                  </TableCell>
                  <TableCell>{formatDate(fromISO8601(requestedAt))}</TableCell>
                  <TableCell>
                    {message && <EllipsisText text={message} lines={2} variant="body2" />}
                  </TableCell>
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
